/**
 * What the analyst decided.
 *
 * This is the point of the whole product. Everything upstream produces a draft; here a person
 * takes responsibility for it, in their own words, and the system records whether they ended up
 * where it pointed.
 *
 * Two deliberate details:
 *
 * - The options do not reuse the model's vocabulary. It suggests *favorable*; a person *aprueba*.
 *   Sharing the words would make a report read as though the model approved a loan.
 * - Whether the decision agrees with the draft is shown **while the analyst chooses**, not after
 *   saving. Someone about to overrule the analysis should see that that is what they are doing.
 */

import { useEffect, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api, friendlyError } from "../../api/client";
import { clearDraft, hasContent, loadDraft, saveDraft } from "../../lib/draft";
import { DECISIONS, RECOMMENDATIONS, label } from "../../lib/labels";
import {
  DECISION_OPTIONS,
  agreesWithSystem,
  decisionChip,
  decisionMeaning,
  formatDateTime,
} from "../../lib/model";
import type { Recommendation } from "../../api/types";
import { Button, Chip, ErrorBanner } from "../../components/ui";

export default function DecisionPanel({
  caseId,
  recommendation,
}: {
  caseId: string;
  recommendation: Recommendation | null;
}) {
  const queryClient = useQueryClient();
  const decision = useQuery({
    queryKey: ["decision", caseId],
    queryFn: () => api.getDecision(caseId),
  });

  // Lo que se está escribiendo se guarda mientras se escribe. Es lo único de esta pantalla que, si
  // la sesión vence o el navegador se cierra, no se puede volver a pedir al servidor.
  const draftKey = `decision.${caseId}`;
  const saved = loadDraft<{ outcome: string; rationale: string; conditions: string }>(draftKey);
  const recovered = hasContent(saved);

  const [editing, setEditing] = useState(recovered);
  const [outcome, setOutcome] = useState<string>(saved?.outcome ?? "");
  const [rationale, setRationale] = useState(saved?.rationale ?? "");
  const [conditions, setConditions] = useState(saved?.conditions ?? "");

  useEffect(() => {
    if (!editing) return;
    saveDraft(draftKey, { outcome, rationale, conditions });
  }, [draftKey, editing, outcome, rationale, conditions]);

  const save = useMutation({
    mutationFn: () =>
      api.recordDecision(caseId, {
        outcome,
        rationale: rationale.trim(),
        conditions: conditions
          .split("\n")
          .map((c) => c.trim())
          .filter(Boolean),
      }),
    onSuccess: async () => {
      // Guardado deja de ser un borrador.
      clearDraft(draftKey);
      setEditing(false);
      await queryClient.invalidateQueries({ queryKey: ["decision", caseId] });
      // The header carries the decision chip, so the legajo itself has to be refetched too.
      await queryClient.invalidateQueries({ queryKey: ["case", caseId] });
      await queryClient.invalidateQueries({ queryKey: ["activity", caseId] });
      await queryClient.invalidateQueries({ queryKey: ["cases"] });
      await queryClient.invalidateQueries({ queryKey: ["metrics"] });
    },
  });

  function startEditing() {
    const current = decision.data;
    setOutcome(current?.outcome ?? "");
    setRationale(current?.rationale ?? "");
    setConditions((current?.conditions ?? []).join("\n"));
    setEditing(true);
  }

  function discard() {
    clearDraft(draftKey);
    setOutcome("");
    setRationale("");
    setConditions("");
    setEditing(false);
  }

  const taken = decision.data;

  if (!editing && taken) {
    const chip = decisionChip(taken)!;
    return (
      <section className="rounded-xl border border-line p-5">
        <div className="flex items-start justify-between gap-4">
          <div>
            <div className="text-[0.7rem] font-semibold tracking-wider text-muted uppercase">
              Decisión del analista
            </div>
            <div className="mt-1.5 flex flex-wrap items-center gap-2">
              <Chip text={chip.text} tone={chip.tone} />
              {taken.agreed_with_system === false ?
                <Chip text="No coincide con la sugerencia" tone="warn" />
              : null}
            </div>
          </div>
          <Button onClick={startEditing}>Cambiar</Button>
        </div>

        <p className="mt-3 text-sm whitespace-pre-line">{taken.rationale}</p>

        {taken.conditions.length ?
          <>
            <div className="mt-3 text-[0.7rem] font-semibold tracking-wider text-muted uppercase">
              Condiciones
            </div>
            <ul className="mt-1 list-disc space-y-0.5 pl-5 text-sm">
              {taken.conditions.map((c, i) => (
                <li key={i}>{c}</li>
              ))}
            </ul>
          </>
        : null}

        <p className="mt-3 text-xs text-muted">
          {taken.decided_by_name ?? "—"} · {formatDateTime(taken.created_at)}
        </p>
      </section>
    );
  }

  if (!editing) {
    return (
      <section className="rounded-xl border border-dashed border-line bg-canvas p-5">
        <div className="text-[0.7rem] font-semibold tracking-wider text-muted uppercase">
          Decisión del analista
        </div>
        <p className="mt-1.5 text-sm text-muted">
          Todavía nadie decidió sobre este legajo. El análisis es un borrador: la decisión es tuya.
        </p>
        <Button variant="primary" className="mt-3" onClick={startEditing}>
          Registrar decisión
        </Button>
      </section>
    );
  }

  const agreement = outcome ? agreesWithSystem(outcome, recommendation) : null;

  return (
    <section className="rounded-xl border border-line p-5">
      <div className="text-[0.7rem] font-semibold tracking-wider text-muted uppercase">
        Registrar decisión
      </div>

      <div className="mt-3 space-y-2">
        {DECISION_OPTIONS.map((option) => (
          <label
            key={option}
            className={`flex cursor-pointer items-start gap-3 rounded-lg border p-3 ${
              outcome === option ? "border-primary bg-canvas" : "border-line hover:bg-canvas"
            }`}
          >
            <input
              type="radio"
              name="outcome"
              value={option}
              checked={outcome === option}
              onChange={() => setOutcome(option)}
              className="mt-0.5"
            />
            <span>
              <span className="block text-sm font-medium">{label(DECISIONS, option)}</span>
              <span className="block text-xs text-muted">{decisionMeaning(option)}</span>
            </span>
          </label>
        ))}
      </div>

      {agreement === false ?
        <p className="mt-3 rounded-lg border border-warn-line bg-warn-bg px-3 py-2 text-sm text-warn">
          Esta decisión no coincide con la sugerencia del sistema (
          {label(RECOMMENDATIONS, recommendation)}). Queda registrada como tal — explicá abajo por
          qué, que es lo que va a leer quien revise el legajo.
        </p>
      : null}

      <label className="mt-4 block text-sm font-medium" htmlFor="rationale">
        Fundamento
      </label>
      <textarea
        id="rationale"
        rows={4}
        value={rationale}
        onChange={(e) => setRationale(e.target.value)}
        placeholder="Por qué decidís esto. Lo lee quien revise el legajo."
        className="mt-1 w-full rounded-lg border border-line px-3 py-2 text-sm focus:border-primary focus:outline-none"
      />

      <label className="mt-4 block text-sm font-medium" htmlFor="conditions">
        Condiciones <span className="font-normal text-muted">(una por línea, opcional)</span>
      </label>
      <textarea
        id="conditions"
        rows={3}
        value={conditions}
        onChange={(e) => setConditions(e.target.value)}
        className="mt-1 w-full rounded-lg border border-line px-3 py-2 text-sm focus:border-primary focus:outline-none"
      />

      {save.isError ?
        <div className="mt-3">
          <ErrorBanner {...friendlyError(save.error)} />
        </div>
      : null}

      <div className="mt-4 flex gap-2">
        <Button
          variant="primary"
          disabled={!outcome || !rationale.trim() || save.isPending}
          onClick={() => save.mutate()}
        >
          {save.isPending ? "Guardando…" : "Guardar decisión"}
        </Button>
        <Button onClick={discard}>Cancelar</Button>
        {recovered ?
          <span className="text-xs text-muted">Recuperamos lo que estabas escribiendo.</span>
        : null}
      </div>
    </section>
  );
}
